import { motion } from "framer-motion";
import ReactTooltip from "react-tooltip";
import { ApiSubscriptionPlan } from "src/api/types";
import useWindowSize from "src/hooks/useWindowSize";
import Badge from "src/components/Badge";
import Button from "src/components/Button";
import useAuth from "src/hooks/selectors/useAuth";
import { Trans, useTranslation } from "react-i18next";
import SubscriptionPlanPrice from "src/components/subscription/SubscriptionPlanPrice";
import Indicator from "src/components/layout/Indicator";
import useSubscription from "src/hooks/selectors/useSubscription";
import { cn } from "src/lib/utils";

interface SubscriptionPlanProps {
	plan: ApiSubscriptionPlan;
	isRecommended?: boolean;
	isSelected?: boolean;
	onSelect?: (plan?: ApiSubscriptionPlan) => void;
}

const SubscriptionPlan = ({
	plan,
	isRecommended,
	isSelected,
	onSelect,
}: SubscriptionPlanProps) => {
	const subscription = useSubscription();
	const { t } = useTranslation("subscription", {
		keyPrefix: "subscription-plan",
	});
	const size = useWindowSize();
	const auth = useAuth();
	const promos = [...(auth?.company?.promos || []), ...(plan?.promos || [])];

	if (size.isPhone && isSelected) {
		return <></>;
	}
	const canUpgrade = plan.statuses.can_upgrade;

	return (
		<motion.div
			data-tip
			data-for={`tooltip-subscription-${plan.slug}`}
			animate={{
				opacity: 1,
				y: 0,
				transition: {
					delay: isRecommended ? 0.3 : 0,
				},
			}}
			initial={{
				opacity: 0,
				y: isRecommended ? -200 : -100,
			}}
			className={cn(
				"rounded-md flex flex-col relative shadow bg-accent border border-border text-accent-foreground my-8",
				isSelected && "my-0",
				isRecommended &&
					"z-[60] bg-background dark:bg-accent text-background-foreground dark:text-accent-foreground border-primary -mx-2 my-0"
			)}
		>
			{plan.promos.includes("blackfriday") && (
				<div
					className={cn(
						"px-10 py-4 bg-dark-accent rounded-t-md",
						isRecommended && "bg-dark"
					)}
				>
					<Trans {...{ t }} i18nKey="promos.blackfriday">
						Tijdelijke <strong>BLACKFRIDAY</strong> deal
					</Trans>
				</div>
			)}
			{!canUpgrade && (
				<ReactTooltip
					id={`tooltip-subscription-${plan.slug}`}
					effect="solid"
				>
					<div className="max-w-[200px] text-center font-bold">
						{t("cant-change")}
					</div>
				</ReactTooltip>
			)}
			{isRecommended && (
				<div className="absolute w-max uppercase top-2 md:top-6 -right-2 md:-right-4 z-[80]">
					<Badge
						variant="primary"
						className="w-max uppercase border-0 text-sm py-2"
					>
						{t("most-common")}
					</Badge>
				</div>
			)}
			<div
				className={cn(
					"px-8 py-8 gap-8 pt-12 flex flex-col flex-1",
					plan.promos.includes("blackfriday") && "pt-6"
				)}
			>
				<SubscriptionPlanPrice
					discount={
						promos?.includes("euro_subscription_deal")
							? {
									price: 0.99,
							  }
							: undefined
					}
					{...{ plan }}
				/>
				<div className="flex flex-col flex-1 gap-4 mb-8">
					{plan.services.map((service) => {
						const isComingSoon = service
							.trim()
							.endsWith("(coming soon)");

						service = service.replace("(coming soon)", "");
						return (
							<div
								key={`${plan.slug}-${service}`}
								className="flex flex-row gap-2"
							>
								<div>
									<i className="fas leading-2 text-primary fa-check"></i>
								</div>
								<p className="flex items-center gap-4">
									{service}

									{isComingSoon && (
										<Indicator className="w-max rounded-sm bg-background text-background-foreground py-3">
											Coming soon
										</Indicator>
									)}
								</p>
							</div>
						);
					})}
				</div>

				{plan.slug === "free" ? (
					<div className="flex flex-col gap-4">
						{!subscription?.id && (
							<Button disabled>{t("button.current")}</Button>
						)}
					</div>
				) : (
					<div className="flex gap-4 flex-col">
						{!isSelected && (
							<>
								<Button
									disabled={!plan.statuses.can_upgrade}
									onClick={() =>
										onSelect &&
										onSelect(
											isSelected
												? ({} as ApiSubscriptionPlan)
												: plan
										)
									}
									className="flex-1"
									type={
										isSelected || !canUpgrade
											? "gray"
											: isRecommended
											? "primary"
											: "white"
									}
								>
									{isSelected ? (
										<>
											<i className="fas fa-arrow-left mr-2"></i>
											{t("button.other-options")}
										</>
									) : (
										<>
											{t("button.choose")}
											<i className="fas fa-arrow-right ml-2"></i>
										</>
									)}
								</Button>
								{isRecommended && plan.discount?.rate ? (
									<div className="flex items-end justify-end gap-3 px-4">
										<div className="flex h-8">
											<svg
												viewBox="0 0 252 117"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													fillRule="evenodd"
													clipRule="evenodd"
													d="M148.722 94.7139L167.506 101.803C160.832 100.54 154.583 98.1156 148.722 94.7139ZM104.924 90.7132C112.4 92.5937 119.741 94.712 127.335 95.7447C119.35 96.2662 112.047 93.7129 104.924 90.7132ZM123.928 67.8692C123.938 64.9014 125.847 65.3054 127.6 65.5884C133.528 66.542 136.506 71.1729 139.311 75.628C141.355 78.8748 143.071 82.5074 143.052 86.5648C143.045 88.1118 142.613 89.3343 140.63 88.1965C135.216 85.089 130.364 81.3413 126.907 76.0494C125.26 73.5282 124.133 70.7746 123.928 67.8692ZM127.363 92.2346C122.526 92.3936 117.91 90.9842 113.3 89.7307C93.3808 84.3132 76.7083 73.3851 61.6623 59.5458C51.6119 50.3007 42.5418 40.1881 33.8108 29.7354C33.0134 28.7824 32.2451 27.7867 31.0286 27.3363C29.6617 26.831 28.6887 26.9251 29.3051 28.7681C29.4556 29.2222 30.0752 29.6546 29.2144 29.903C26.2457 30.7611 27.0103 32.9262 27.5018 34.8922C28.4229 38.582 30.8332 41.5187 32.8866 44.5676C38.6385 53.1105 44.7192 61.4439 51.8406 68.9074C58.4523 75.8357 65.9936 81.7284 73.6955 87.4065C78.5982 91.0208 83.6353 94.6294 89.4495 96.3228C93.3121 97.4477 96.4067 99.8703 100.175 101.012C107.398 103.2 114.616 105.302 122.193 105.85C128.43 106.3 134.347 105.162 139.83 102.147C141.637 101.152 143.321 101.109 145.126 101.888C149.991 103.985 154.937 105.932 159.869 107.797C167.893 110.834 176.362 112.583 184.842 113.995C198.369 116.246 211.939 117.111 225.548 114.649C233.547 113.202 241.339 111.255 247.426 105.312C249.029 103.747 250.228 102.011 250.193 99.6452C250.18 98.6257 249.871 97.8493 248.881 97.4061C247.71 96.8837 247.592 97.7178 247.036 98.473C243.221 103.653 237.27 104.241 231.606 105.309C215.998 108.252 200.547 106.525 185.154 103.567C173.374 101.303 161.929 97.911 150.894 93.1838C150.018 92.8085 148.8 92.8188 149.563 91.1651C152.22 85.4033 151.907 79.461 149.92 73.65C146.854 64.68 141.737 57.3638 132.157 54.4413C127.01 52.8706 123.532 54.4937 120.424 58.8498C112.504 69.9462 116.545 82.1052 125.24 89.4893C126.019 90.1496 126.798 90.8099 127.575 91.4691C127.505 91.725 127.433 91.9798 127.363 92.2346Z"
													fill="url(#paint0_linear_4535_3042)"
												/>
												<path
													fillRule="evenodd"
													clipRule="evenodd"
													d="M29.0756 13.1055C23.0539 11.098 17.4363 8.32496 12.4154 4.42996C17.9684 7.32142 23.5214 10.214 29.0756 13.1055ZM16.1351 38.3331C16.1006 31.9029 14.9698 25.5908 14.0874 19.2511C13.9554 18.3035 13.2994 17.2619 14.5005 16.4561C15.6545 15.6826 16.5211 16.507 17.2848 17.0283C21.4498 19.8658 26.2146 21.3221 30.7705 23.2982C33.6482 24.5477 36.3129 26.351 39.3086 27.5444C43.9215 29.3804 48.4022 31.5645 52.8824 33.7168C55.153 34.8071 57.8357 34.7872 59.7923 36.8255C61.7121 38.8245 63.8385 38.2675 66.2964 35.0312C67.5515 33.38 68.0915 31.1594 66.03 29.538C64.3346 28.2048 62.5066 26.9797 60.5867 26.0045C47.8799 19.552 35.1214 13.2022 22.4075 6.76441C19.5536 5.31966 16.794 3.68083 14.0106 2.09847C8.94996 -0.77905 6.77547 -0.0658023 3.95945 5.05848C1.77351 9.0364 1.5377 12.9947 2.8911 17.2935C5.49521 25.5687 7.86375 33.9184 8.86772 42.5751C9.06845 44.3099 9.02531 46.0713 9.15876 47.8163C9.31148 49.8369 10.2491 50.3679 12.001 49.2096C15.8856 46.6446 16.2245 45.7641 16.1351 38.3331Z"
													fill="url(#paint1_linear_4535_3042)"
												/>
												<defs>
													<linearGradient
														id="paint0_linear_4535_3042"
														x1="139.237"
														y1="28.9045"
														x2="137.796"
														y2="114.968"
														gradientUnits="userSpaceOnUse"
													>
														<stop stopColor="#F88C5E" />
														<stop
															offset="1"
															stopColor="#F9B739"
														/>
													</linearGradient>
													<linearGradient
														id="paint1_linear_4535_3042"
														x1="35.0872"
														y1="0.850417"
														x2="34.2604"
														y2="50.2124"
														gradientUnits="userSpaceOnUse"
													>
														<stop stopColor="#F88C5E" />
														<stop
															offset="1"
															stopColor="#F9B739"
														/>
													</linearGradient>
												</defs>
											</svg>
										</div>

										<strong className="uppercase text-orange-light">
											{t("footer.save-cta", {
												save: `${plan.discount.rate}%`,
											})}
										</strong>
									</div>
								) : null}
							</>
						)}
						{/* {tenant.providers.payments === "OPP" && (
							<div className="flex flex-col">
								{can_use_trial &&
									!promos.includes(
										"euro_subscription_deal"
									) && (
										<strong
											className={cn(
												"text-center mb-0 text-accent-foreground"
												// isRecommended && "text-primary"
											)}
										>
											{t("trial", {
												days: 14,
											})}
										</strong>
									)}
								{interval !== "YEARLY" && (
									<span className="text-center">
										{plan.cancel_text}
									</span>
								)}
							</div>
						)} */}
						{/* <div className="flex flex-col">
							{can_use_trial &&
								!promos.includes("euro_subscription_deal") &&
								!promos.includes("blackfriday") && (
									<strong
										className={`text-center text-${
											isRecommended ? "orange" : "primary"
										} mb-0`}
									>
										{t("trial", {
											days: 14,
										})}
									</strong>
								)}
							{interval !== "YEARLY" && !plan.discount && (
								<span className="text-center">
									{plan.cancel_text}
								</span>
							)}
						</div> */}
					</div>
				)}
			</div>
		</motion.div>
	);
};

export default SubscriptionPlan;
